const PDF_FRAME_URL = "/wellness/fwa/pdf-frame"
const DEFAULT_TIMEOUT_MS = 10000

/**
 * Open a hidden, same-origin iframe at `/wellness/fwa/pdf-frame` and
 * resolve once the shell's self-contained bundle has installed its
 * `window.BanzaiPdfGen.generate` API. Reject (and remove the iframe) if
 * the load event does not fire or BanzaiPdfGen never appears within
 * `timeoutMs`.
 *
 * Same architecture as the FWA Coach: the iframe owns its own React,
 * CSS modules, and PDF rendering — the parent only calls into it.
 */
export const openPdfFrame = (opts = {}) => {
  const {timeoutMs = DEFAULT_TIMEOUT_MS} = opts
  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe")
    iframe.setAttribute("aria-hidden", "true")
    iframe.setAttribute("title", "PDF render frame")
    iframe.style.cssText = `
      position: fixed;
      top: 0;
      left: -10000px;
      width: 1px;
      height: 1px;
      border: 0;
      pointer-events: none;
    `
    iframe.src = PDF_FRAME_URL

    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
    }

    const timer = setTimeout(() => {
      iframe.removeEventListener("load", onLoad)
      cleanup()
      reject(new Error(`openPdfFrame: BanzaiPdfGen not ready within ${timeoutMs}ms`))
    }, timeoutMs)

    const onLoad = () => {
      try {
        const win = iframe.contentWindow
        const api = win && win.BanzaiPdfGen
        if (!api || typeof api.generate !== "function") {
          throw new Error("openPdfFrame: BanzaiPdfGen API missing — shell bundle failed to load")
        }
        clearTimeout(timer)
        resolve(iframe)
      } catch (err) {
        clearTimeout(timer)
        cleanup()
        reject(err)
      }
    }

    iframe.addEventListener("load", onLoad, {once: true})
    document.body.appendChild(iframe)
  })
}

export const closePdfFrame = (iframe) => {
  if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe)
}
